import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Steps, { type StepsProps } from "@cloudscape-design/components/steps"
import { type ReactNode, useMemo } from "react"
import type { DTADetails } from "../../../services/dtaApi"
import { stageBadgeColor } from "../../../components/shared/dtaDisplay"
import TimelineDotIcon from "../../../components/shared/TimelineDotIcon"
import { formatUserId } from "../../../utils/userFormatter"

interface ActivityTimelineProps {
  /**
   * The DTA's status-transition history (most recent first as stored on the record).
   */
  statusHistory: DTADetails["status_history"]
  /**
   * When set, only the most recent `limit` events are shown (used by the Overview recent-activity
   * card). Omit to show the full history (Audit Trail tab).
   */
  limit?: number
  /**
   * Accessible label for the timeline.
   */
  ariaLabel?: string
}

/**
 * Renders the DTA activity timeline: stage changes as a full-size stage pill (orange dot), and
 * in-stage edits/approvals nested beneath as smaller, indented sub-task rows (grey dot + reason) so
 * they read as work done within a stage. Shared by the Overview "Recent activity" card (truncated)
 * and the Audit Trail tab (full).
 */
const ActivityTimeline = ({
  statusHistory,
  limit,
  ariaLabel = "DTA activity",
}: ActivityTimelineProps) => {
  const steps = useMemo<Array<StepsProps.Step & { icon: ReactNode }>>(() => {
    const history = statusHistory ?? []
    const visible = limit !== undefined ? history.slice(0, limit) : history
    return visible.map((h, idx) => {
      // A row opens a stage (rendered as a pill) when it is the oldest row of a
      // contiguous same-stage run: the next-older row is a different stage, or
      // there is none. Derived from workflow_stage (always present) so every
      // stage the DTA passed through shows as a milestone, including the initial
      // one, even when an explicit creation/transition event is missing. Other
      // rows nest beneath as smaller, indented sub-tasks.
      const stage = h.workflow_stage
      const stageEntry = stage !== visible[idx + 1]?.workflow_stage
      const header: ReactNode = stageEntry ? (
        <Badge color={stageBadgeColor(stage)}>{stage}</Badge>
      ) : (
        <Box padding={{ left: "l" }} fontSize="body-s" color="text-body-secondary">
          {h.reason || "Updated"}
        </Box>
      )
      return {
        status: "info" as const,
        statusIconAriaLabel: "Activity event",
        icon: <TimelineDotIcon variant={stageEntry ? "primary" : "muted"} />,
        header,
        // Bottom padding gives breathing room between entries (Steps has no gap prop); in-stage rows
        // also indent left to sit under their stage pill.
        details: (
          <Box
            padding={stageEntry ? { bottom: "m" } : { bottom: "m", left: "l" }}
            color="text-body-secondary"
            fontSize="body-s"
          >
            {formatUserId(h.changed_by)} • {new Date(h.changed_at).toLocaleString()}
          </Box>
        ),
      }
    })
  }, [statusHistory, limit])

  if (steps.length === 0) {
    return <Box color="text-body-secondary">No activity</Box>
  }

  return (
    <Steps
      steps={steps}
      ariaLabel={ariaLabel}
      // Steps types renderStep's arg as the base Step; the objects passed carry our per-step icon.
      renderStep={(step) => {
        const { icon } = step as StepsProps.Step & { icon: ReactNode }
        return { header: step.header, details: step.details, icon }
      }}
    />
  )
}

export default ActivityTimeline
